import React, { useEffect, useState, Suspense, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { useSendCcmPatientCall } from 'infrastructure/hooks/use-send-ccm-patient-call';
import { PatientSelect } from 'infrastructure/classes/select/patient-select';
import {
  AgencyServiceDeliveryModel,
  ClinicServiceDeliveryModel,
  CommentType,
  ModuleType,
  RpmStatuses,
} from 'infrastructure/enums';
import { prepareDoctorName, isValidGuid } from 'infrastructure/functions';
import { SocketRoutes } from 'services/wsService';
import CallDashboardCallTypeForm from 'components/molecules/call-dashboard/forms/call-type';
import BaseLoader from 'components/atoms/base-loader';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useConfirmExit } from 'infrastructure/hooks/utils';
import { useTranslation } from 'react-i18next';
import { useCallDashboard } from '@accuhealth/call-dashboard-ui';
import useUser from 'utils/useUser';
import { useSendComment } from 'infrastructure/hooks/comments/use-send';
import CallDashboardSaasInfo from 'components/organisms/call-dashboard/saas-info';
import { usePatientCallInfo } from 'infrastructure/hooks/patient/use-call-info';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';

import s from './styles.module.scss';
import CallDashboardDetailTabs from '../../components/organisms/call-dashboard/tabbed-forms';

import type { IPatientCallAttribute } from 'infrastructure/interfaces';
import type { RootState } from 'store';
import type { ICallDashboardCallTypeFormValues } from 'components/molecules/call-dashboard/forms/call-type';

const CallDashboard = React.lazy(() =>
  import('@accuhealth/call-dashboard-ui').then((module) => ({
    default: module.CallDashboard,
  })),
);

const CallDashboardPage: React.FC = () => {
  const { isAdmin } = useUser();
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();

  const { agent, isInitialized, contact: activeContact } = useCallDashboard();
  const showConfirm = !agent?.isOffline && isInitialized && !activeContact;

  const [isUnknownNumber, setIsUnknownNumber] = useState<boolean>(false);
  const [showOptionsSelection, setShowOptionsSelection] =
    useState<boolean>(false);
  const [callId, setCallId] = useState<string | undefined>();
  const [sendingInfo, setSendingInfo] = useState<boolean>(false);
  const [patientGuid, setPatientGuid] = useState<string | undefined>();
  const [patients, setPatients] = useState<PatientSelect[]>([]);
  const infoRef = useRef<{
    patientGuid: string | undefined;
    isRpmCall: boolean | undefined;
    isCall: boolean;
    isOutboundCall: boolean;
  }>({
    isCall: false,
    isOutboundCall: false,
    patientGuid: undefined,
    isRpmCall: undefined,
  });
  const promiseRef = useRef<{ resolve: (res: boolean) => void } | null>(null);

  const socket = useSelector((state: RootState) => state.socket);

  const { sendComment } = useSendComment();

  const { sendInfo } = useSendCcmPatientCall();

  const { data: callInfo, isFetching } = usePatientCallInfo({ patientGuid });

  const isSaaS =
    callInfo?.clinicServiceDeliveryModel === ClinicServiceDeliveryModel.SaaS ||
    callInfo?.agencyServiceDeliveryModel === AgencyServiceDeliveryModel.SaaS;

  const { patientInfo, loading, error } = usePatientInfo({
    patientGuid: !isSaaS && !isFetching ? patientGuid ?? '' : '',
  });

  const showInfo =
    callInfo ||
    (!!patientInfo && !error) ||
    isUnknownNumber ||
    !!patients.length ||
    (loading && !!patientGuid);

  const handleStopCall = () => {
    infoRef.current.isCall = false;
  };
  const handleSetGuid = (guid?: string) => {
    if (isValidGuid(guid ?? '')) {
      infoRef.current.patientGuid = guid;
      setPatientGuid(guid);
    } else {
      infoRef.current.patientGuid = undefined;
      setPatientGuid(undefined);
    }
  };
  const handleSubmitInfo = async (data: ICallDashboardCallTypeFormValues) => {
    infoRef.current.isCall = true;
    infoRef.current.isRpmCall = data.isRpm;
    if (data.patientGuid) {
      handleSetGuid(data.patientGuid);
    }
    if (promiseRef.current) {
      setTimeout(() => {
        promiseRef!.current!.resolve(true);
      }, 300);
    }

    if (data.patientGuid && callId) {
      await handleSendInfo(data.isRpm!, callId, data.patientGuid).then(() => {
        setShowOptionsSelection(false);
      });
    }
  };
  const sendCcmInfo = async (contactId: string, patientId: string) => {
    const callData = {
      callId: contactId,
      patientGuid: patientId,
    };
    await sendInfo(callData);
  };
  const sendRpmInfo = async (contactId: string, patientId: string) => {
    const callData = {
      commentType: ModuleType.RPM,
      message: contactId,
      type: CommentType.Call,
      patientGuid: patientId,
    };
    await sendComment(callData, { showResult: false, withTimeout: false });
  };
  const handleSendInfo = async (
    isRpm: boolean,
    contactId: string,
    patientId?: string,
  ) => {
    if (patientId) {
      try {
        setSendingInfo(true);
        if (isRpm) {
          await sendRpmInfo(contactId, patientId);
        } else {
          await sendCcmInfo(contactId, patientId);
        }
      } finally {
        setSendingInfo(false);
      }
    }
  };
  const handleSendWs = (isRpm: boolean, patientId?: string) => {
    if (socket.client && patientId) {
      socket.client.send(SocketRoutes.OnCallStarted, {
        isRpmCall: isRpm,
        patientGuid: patientId,
      });
    }
  };
  const handleCloseWs = (isRpm: boolean, patientId?: string) => {
    if (socket.client && patientId) {
      socket.client.send(SocketRoutes.OnCallEnded, {
        isRpmCall: isRpm,
        patientGuid: patientId,
      });
    }
  };
  const handleBeforeCall = () => {
    setShowOptionsSelection(true);
    infoRef.current.isOutboundCall = true;
    return new Promise<boolean>((resolve) => {
      promiseRef.current = { resolve };
    });
  };
  const handleClearPromise = () => {
    setShowOptionsSelection(false);
    if (promiseRef.current) {
      promiseRef.current.resolve(false);
    }
    promiseRef.current = null;
  };
  const handleSetPatients = (data?: IPatientCallAttribute[]) => {
    if (data) {
      const mapped = data.map(
        (el) =>
          new PatientSelect({
            guid: el.guid,
            label: prepareDoctorName({
              title: el.title,
              firstName: el.firstName,
              lastName: el.lastName,
            }),
            record: {
              birthDate: el.birthDate,
              status: RpmStatuses.Active,
            },
          }),
      );
      setPatients(mapped);
      handleSetGuid(mapped[0].guid);
    }
  };

  const handleClearStates = () => {
    handleSetGuid(undefined);
    setPatients([]);
    setIsUnknownNumber(false);
    setShowOptionsSelection(false);
    infoRef.current.isCall = false;
    infoRef.current.isOutboundCall = false;
    infoRef.current.isRpmCall = undefined;
    infoRef.current.patientGuid = undefined;
  };

  useConfirmExit(async () => {
    const res: boolean = await showAlert({
      title: t('labels.confirm'),
      message: t('messages.changeStatus'),
      successBtnLabel: t('labels.yes'),
      rejectBtnLabel: t('labels.no'),
    });

    if (res) {
      agent.setOffline();
    }

    return res !== undefined;
  }, showConfirm);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <div className={s.dashboard}>
      <Suspense fallback={<BaseLoader loading relative />}>
        <CallDashboard
          topPadding={60}
          contactHandlers={{
            onInitContact: (contact) => {
              if (contact.attributes?.customerId?.value)
                handleSetGuid(contact.attributes?.customerId?.value);
              else if (!infoRef.current.isOutboundCall) {
                handleSetGuid(undefined);
                if (!contact.attributes.customers?.value)
                  setIsUnknownNumber(true);
              }
              if (contact.attributes.customers?.value) {
                handleSetPatients(
                  JSON.parse(contact.attributes.customers?.value),
                );
              }
            },
            onConnected: (contact) => {
              if (
                typeof infoRef.current.isRpmCall === 'boolean' &&
                infoRef.current.patientGuid
              ) {
                handleSendInfo(
                  infoRef.current.isRpmCall,
                  contact.id,
                  infoRef.current.patientGuid!,
                );
                handleSendWs(
                  infoRef.current.isRpmCall,
                  infoRef.current.patientGuid!,
                );
              }
            },
            onDestroy: () => {
              handleStopCall();
              handleClearStates();
              if (typeof infoRef.current.isRpmCall === 'boolean') {
                handleCloseWs(
                  infoRef.current.isRpmCall,
                  infoRef.current.patientGuid!,
                );
              }
            },
            onEnded: (contact) => {
              handleStopCall();
              if (typeof infoRef.current.isRpmCall === 'boolean') {
                handleCloseWs(
                  infoRef.current.isRpmCall,
                  infoRef.current.patientGuid!,
                );
              } else {
                setShowOptionsSelection(true);
                setCallId(contact.id);
              }
            },
            onContactHistoryClick: (contactHistory) => {
              handleClearPromise();
              if (
                !Array.isArray(contactHistory.customer) &&
                contactHistory.customer
              ) {
                handleSetGuid(contactHistory.customer.externalId!);
              }
            },
            onContactClick: (contact) => {
              handleClearPromise();
              handleSetGuid(contact.externalId!);
            },
          }}
          contentHandlers={{
            canStartCall: handleBeforeCall,
            onTabChange: (_, isUserEvent) => {
              if (isUserEvent) {
                handleClearStates();
              }
              handleClearPromise();
            },
          }}
          contentOptions={{
            disablePrimaryCallBtn: patientInfo?.doNotCall,
            disableSecondaryCallBtn: patientInfo?.doNotCall,
            showCallScript: false,
            canModifiedProfiles: isAdmin,
          }}
        >
          <BaseSpinWrapper spinning={isFetching}>
            {showInfo ? (
              <>
                {showOptionsSelection ? (
                  <CallDashboardCallTypeForm
                    sending={sendingInfo}
                    patients={patients}
                    onSubmit={handleSubmitInfo}
                    patientGuid={patientGuid}
                    isUnknownNumber={isUnknownNumber}
                  />
                ) : (
                  <>
                    {isSaaS ? (
                      <CallDashboardSaasInfo info={callInfo} />
                    ) : (
                      <CallDashboardDetailTabs
                        patients={patients}
                        patientGuid={infoRef.current.patientGuid!}
                        isUnknownNumber={isUnknownNumber}
                      />
                    )}
                  </>
                )}
              </>
            ) : undefined}
          </BaseSpinWrapper>
        </CallDashboard>
      </Suspense>
      {Alert}
    </div>
  );
};

export default CallDashboardPage;
